import { useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { signMediaUrls, SIGNED_MEDIA_REFRESH_MS } from "@/lib/media";
import {
  intendedChannelMediaSyncPreview,
  listingMediaPreviewCards,
  operatorPreviewStatus,
  type EffectiveListingMediaRow,
} from "@/lib/listing-media-preview";
import { isInheritingMedia, type ChannelListing } from "@/lib/channels";

export function ListingMediaPanel(props: {
  orgId: string;
  listingId: string;
  listing: ChannelListing;
  media: EffectiveListingMediaRow[];
  gallery: { media_asset_id: string; is_primary: boolean; media_assets: { original_filename: string | null } | { original_filename: string | null }[] | null }[];
  canManage: boolean;
  actionBusy: boolean;
  setActionBusy: (v: boolean) => void;
  setActionError: (v: string) => void;
}) {
  const queryClient = useQueryClient();
  const [signedByKey, setSignedByKey] = useState<Record<string, string>>({});
  const [signing, setSigning] = useState(true);
  const [primaryDraft, setPrimaryDraft] = useState<string | null>(null);

  const cards = useMemo(
    () => listingMediaPreviewCards({ mediaStrategy: props.listing.media_strategy, rows: props.media }),
    [props.listing.media_strategy, props.media],
  );
  const syncPreview = intendedChannelMediaSyncPreview({
    mediaStrategy: props.listing.media_strategy,
    cards,
  });

  const listingPrimaryId =
    props.media.find((m) => m.source === "listing_custom" && m.is_primary)?.media_asset_id
    ?? cards.find((c) => c.isPrimary)?.media_asset_id
    ?? "";
  const selectedPrimary = primaryDraft ?? listingPrimaryId;

  useEffect(() => {
    const keys = cards.map((c) => c.storage_key).filter((k): k is string => !!k);
    if (keys.length === 0) {
      setSigning(false);
      return;
    }
    let cancelled = false;
    setSigning(true);
    void signMediaUrls(keys).then((urls) => {
      if (!cancelled) {
        setSignedByKey(urls);
        setSigning(false);
      }
    });
    const t = window.setInterval(() => {
      void signMediaUrls(keys).then((urls) => {
        if (!cancelled) setSignedByKey(urls);
      });
    }, SIGNED_MEDIA_REFRESH_MS);
    return () => {
      cancelled = true;
      window.clearInterval(t);
    };
  }, [cards]);

  const inheriting = isInheritingMedia(props.listing);

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-4">
      <h3 className="text-sm font-semibold text-slate-800">Media</h3>
      <p className="text-xs text-slate-500">
        Previews use the same Product Master signed-URL resolver. Opening this screen does not change inherit/override.
        Channel media overrides stay until you click Use Product Master or save a channel primary.
      </p>
      <div className="flex flex-wrap items-center gap-2">
        {inheriting ? (
          <span className="text-xs bg-emerald-50 text-emerald-700 px-1.5 py-0.5 rounded-full">Product Master / inherited</span>
        ) : (
          <span className="text-xs bg-amber-50 text-amber-700 px-1.5 py-0.5 rounded-full">Channel-specific media override</span>
        )}
        {!inheriting && props.canManage && (
          <button
            type="button"
            className="text-xs font-medium text-emerald-700"
            disabled={props.actionBusy}
            onClick={async () => {
              props.setActionBusy(true);
              props.setActionError("");
              const { error: rpcErr } = await supabase.rpc("reset_listing_field_to_inherit", {
                p_org_id: props.orgId,
                p_listing_id: props.listingId,
                p_field: "media",
              });
              props.setActionBusy(false);
              if (rpcErr) props.setActionError(rpcErr.message);
              else queryClient.invalidateQueries({ queryKey: ["listing-detail", props.orgId, props.listingId] });
            }}
          >
            Use Product Master
          </button>
        )}
      </div>

      {props.gallery.length > 0 && props.canManage && (
        <div className="space-y-2">
          <select value={selectedPrimary} onChange={(e) => setPrimaryDraft(e.target.value)} className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm">
            <option value="">Inherit canonical primary image</option>
            {props.gallery.map((g) => {
              const name = Array.isArray(g.media_assets) ? g.media_assets[0]?.original_filename : g.media_assets?.original_filename;
              return (
                <option key={g.media_asset_id} value={g.media_asset_id}>
                  {name || g.media_asset_id}{g.is_primary ? " (canonical primary)" : ""}
                </option>
              );
            })}
          </select>
          {selectedPrimary && (
            <button
              type="button"
              onClick={async () => {
                props.setActionBusy(true);
                props.setActionError("");
                try {
                  const { error: rpcErr } = await supabase.rpc("set_listing_channel_primary_from_asset", {
                    p_org_id: props.orgId,
                    p_listing_id: props.listingId,
                    p_media_asset_id: selectedPrimary,
                  });
                  if (rpcErr) throw rpcErr;
                  queryClient.invalidateQueries({ queryKey: ["listing-detail", props.orgId, props.listingId] });
                } catch (err) {
                  props.setActionError((err as Error).message);
                }
                props.setActionBusy(false);
              }}
              className="px-3 py-2 bg-emerald-600 text-white text-xs font-medium rounded-lg"
            >
              Save channel primary (does not publish)
            </button>
          )}
        </div>
      )}

      <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-sm text-slate-600 space-y-1">
        <p className="text-xs font-semibold text-slate-700">Sync preview (no provider write)</p>
        <p className="text-xs">{syncPreview.reason}</p>
        <p className="text-xs">Primary: {syncPreview.primaryFilename || "—"}</p>
        <p className="text-xs">Intended images: {syncPreview.filenames.join(" · ") || "—"}</p>
      </div>

      {cards.length === 0 ? (
        <p className="text-sm text-slate-500">
          No media assigned. {inheriting ? "Will inherit from Product Master when published." : ""}
        </p>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {cards.map((card) => {
            const preview = signing
              ? { kind: "missing" as const, src: null, message: "Loading preview…" }
              : operatorPreviewStatus(card.storage_key, signedByKey);
            return (
              <div key={card.media_asset_id} className="border border-slate-200 rounded-lg overflow-hidden">
                <div className="aspect-square bg-slate-100 flex items-center justify-center relative">
                  {preview.kind === "ready" && preview.src ? (
                    <img src={preview.src} alt={card.original_filename || ""} className="w-full h-full object-cover" />
                  ) : (
                    <div className="px-2 text-center">
                      <p className="text-[11px] text-amber-800">{preview.message}</p>
                    </div>
                  )}
                  {card.isPrimary && (
                    <span className="absolute top-2 left-2 text-[10px] font-semibold bg-emerald-600 text-white px-2 py-0.5 rounded-full">Primary</span>
                  )}
                </div>
                <div className="p-2 space-y-1">
                  <p className="text-xs font-medium text-slate-700 truncate">{card.original_filename || "Image"}</p>
                  <p className="text-[10px] text-slate-500">
                    {card.authority === "channel_override" ? "Channel override" : "Product Master / inherited"}
                    {" · "}
                    {card.galleryKind === "primary" ? "Primary image" : "Additional gallery"}
                  </p>
                  {card.duplicatePresentation && (
                    <p className="text-[10px] text-slate-400">Same asset on listing and Product Master — both records kept.</p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
